/**
 * MOCK/TEMPORARY — the public music catalogue behind the Home page.
 *
 * ============================================================================
 * ONE CATALOGUE, THREE VIEWS. Featured, Trending and Explore are not three datasets:
 * they are three orderings of one list, and this module is the boundary that list comes
 * through. lib/discovery does the deciding (which songs are eligible, how each view is
 * ordered); this file is the request that will one day be a real one, and the place that
 * states what the app may claim about it.
 *
 * THERE IS NO DISCOVERY BACKEND, AND THIS MODULE'S JOB IS TO SAY SO. The app has no
 * cross-account data and no recommendation service, and the plays and Loves it does count
 * are this browser's. So there are two ways to build the Home page: invent a plausible feed
 * of other people's songs with plausible-looking numbers on it, or show what genuinely
 * qualifies and put the missing half on the screen. This is the second.
 *
 * WHAT IS REAL HERE. The eligibility rule and the ordering. lib/discovery admits a song
 * only when it is finished AND its owner published it (`status === 'ready' && visibility
 * === 'public'`), and ranks what is left by fields the app actually stores — plays, Loves,
 * creation order (see lib/engagement). Whatever comes back is a true statement about the
 * catalogue, including when the true answer is "nothing", and every count on it is one this
 * app incremented rather than one it made up.
 *
 * WHICH TODAY MEANS: EMPTY UNTIL SOMEBODY PUBLISHES. The seed library is entirely private
 * (data/songs.ts, deliberately — nothing in that fixture says its owner chose to publish),
 * so a fresh session's catalogue is empty and the Home page renders its empty state. The
 * Library's "Make discoverable" action is what fills it, one song at a time, on a real
 * decision by the person who owns the track.
 *
 * That paragraph used to say "nothing sets `visibility: 'public'` because there is no
 * Publish action", and it was left standing after the action shipped. Corrected here rather
 * than deleted, because the empty-by-default part is still true and is still the reason the
 * Home page needs its empty state.
 *
 * AND THE CLIENT CHECK IS NOT A SECURITY BOUNDARY. `isPubliclyDiscoverable` runs in the
 * browser: it governs what is displayed, not what a server would hand out. The public query
 * must enforce the same two conditions itself — that is the
 * `client-side-visibility-filter` blocker, and it is a requirement on the backend rather
 * than a caveat about this file.
 *
 * WHAT IS NOT REAL. The word "trending", in the sense a music platform means it. Nothing
 * here is aggregated across accounts, because there are no other accounts to aggregate,
 * and nothing is personalised by a model. describeDiscoveryBlockers() states all of it, in
 * the shape generationApi's blockers use, and the Home page renders them next to the
 * sections rather than in a footnote — for the reason generationApi gives: a hardcoded
 * limitation is one that will still be on the screen the day it stops being true.
 *
 * THE ENDPOINT THIS BECOMES. One request, replacing every list:
 *
 *   GET /discovery/showcase   PUBLIC or authed — returns the catalogue plus its featured,
 *                             trending and explore views over PUBLISHED songs across
 *                             accounts, ranked and filtered server-side
 *
 * See docs/api-contract.md §22.
 *
 * THE LIBRARY IS NO LONGER A PARAMETER, which was Batch 7 §1.6 and is the one change here
 * that is about authority rather than about wiring. `getShowcase(songs, …)` took the
 * caller's library and ranked it, so the BROWSER decided what was public: whatever a
 * component passed in was the catalogue. Now the read queries the mock's own songs table
 * (./mockCatalogue) and applies the eligibility rule to it, which is the shape a real
 * endpoint has — no caller can hand this function a catalogue, and a song reaches the
 * public list only by having been published through the write seam.
 *
 * `saves` remains a parameter and is the last piece of client-supplied ranking input; the
 * `client-supplied-save-counts` blocker below is what says so on screen.
 * ============================================================================
 */
import { API_BASE_URL, USE_MOCK_API } from '../config';
import { buildShowcase, type Showcase, type ShowcaseOptions } from '../lib/discovery';
import { applyDemoSongStatOverrides, applyDemoSongStatOverridesToList } from '../lib/demoSongStats';
import { hydratePersistedSongLengths } from '../lib/songRuntime';
import type { Page, PageRequest, Song } from '../types/domain';
import { http } from './http';
import { assertMock, mockDelay } from './mock';
import { mockCatalogueSongs } from './mockCatalogue';

/** One reason discovery is not the real thing yet. */
export interface DiscoveryBlocker {
  id:
    | 'no-backend'
    | 'client-side-visibility-filter'
    | 'client-supplied-save-counts'
    | 'no-recommendation-service';
  /** One sentence, written to be read on screen next to the sections. */
  summary: string;
  /** What would have to exist for the blocker to go away. */
  resolution: string;
}

/** What the Home page can and cannot claim about its three sections. */
export interface DiscoverySupport {
  /**
   * True only when the lists come from a real catalogue ranked server-side.
   *
   * Never true in this build, and deliberately not derived from `API_BASE_URL` alone —
   * see audioEditApi.supports() on why "a base URL was set" is not evidence that an
   * endpoint answers. It is `blockers.length === 0`, and three of the blockers below are
   * unconditional.
   */
  catalogueAvailable: boolean;
  /**
   * True when ranking reflects other listeners rather than the viewer's own library.
   * This is the specific claim the word "Trending" makes, tracked separately so the UI
   * can soften that one heading without hiding the section.
   */
  rankingIsSocial: boolean;
  blockers: DiscoveryBlocker[];
}

function withDemoSongStats(showcase: Showcase): Showcase {
  return {
    ...showcase,
    catalogue: applyDemoSongStatOverridesToList(showcase.catalogue),
    featured: showcase.featured ? applyDemoSongStatOverrides(showcase.featured) : null,
    featuredRail: applyDemoSongStatOverridesToList(showcase.featuredRail),
    trending: applyDemoSongStatOverridesToList(showcase.trending),
    explore: applyDemoSongStatOverridesToList(showcase.explore),
  };
}

function withDemoSongStatsPage(page: Page<Song>): Page<Song> {
  return {
    ...page,
    items: applyDemoSongStatOverridesToList(page.items),
  };
}

/** Everything still missing between this module and a real discovery feed. */
export function describeDiscoveryBlockers(): DiscoveryBlocker[] {
  const blockers: DiscoveryBlocker[] = [];

  if (!API_BASE_URL) {
    blockers.push({
      id: 'no-backend',
      summary: 'No backend is configured, so the catalogue can only read this browser.',
      resolution: 'Set VITE_API_BASE_URL and implement GET /discovery/showcase.',
    });
  }

  /*
    Unconditional, all three. A backend appearing tomorrow would not give the app a
    server-enforced visibility rule, server-counted saves or a ranking model — and those are
    what "public" and "Trending" imply.

    `no-publish-action` USED TO BE ON THIS LIST AND HAS BEEN REMOVED, because it stopped being
    true: the Library's "Make discoverable" action publishes a song through
    songsStore.setVisibility, so a user can now put a track in the public catalogue. Leaving
    the blocker up would have been the failure its own resolution warns about — a hardcoded
    limitation still on the screen the day it stops being one. What replaced it is
    `client-supplied-save-counts`, which is a gap that genuinely remains.
  */
  blockers.push({
    /*
      THE ONE BLOCKER THAT IS A SECURITY STATEMENT rather than a missing feature.

      `lib/discovery.isPubliclyDiscoverable` runs in the browser, on songs the browser
      already holds, so it decides what is DISPLAYED and cannot decide what is SENT. Right
      now that is harmless — the only library is the viewer's own — but the moment a real
      endpoint returns other accounts' songs, a private track that arrives in the response
      has already leaked, whatever the client does with it afterwards. Stated on screen
      rather than in a comment because the person who builds that endpoint has to know the
      client check is not doing this job for them.
    */
    id: 'client-side-visibility-filter',
    summary:
      'The public/private rule is applied in this browser, so it controls what is shown rather than what a server would be willing to send.',
    resolution:
      'Enforce status = ready AND visibility = public in the query behind GET /discovery/showcase, and never return a private song to a public caller.',
  });
  blockers.push({
    /*
      THE LAST CLIENT-SUPPLIED INPUT TO THE RANKING, and the reason it gets a blocker of its
      own now that the library parameter is gone. §1.6 removed `songs` from `getShowcase`, so
      the browser no longer decides WHICH songs are public — but `saves` is still passed in,
      and it is one of the three signals that decides which public song is Featured. A number
      a client supplies is a number a client can raise, so this is a ranking anyone could
      promote themselves in.

      Narrower than the old `no-publish-action` blocker it replaces on the list, and true,
      which is the trade: it names the input that is still the caller's rather than a feature
      that now exists.
    */
    id: 'client-supplied-save-counts',
    summary:
      'How often a song has been saved is counted in this browser and sent with the request, so the Featured ranking reads a figure the client supplies.',
    resolution:
      'Count playlist saves server-side and compute the Featured ranking from them; GET /discovery/showcase must not accept save counts from a caller.',
  });
  blockers.push({
    /*
      UPDATED FOR ENGAGEMENT RANKING, and the change is narrower than it looks. The app now
      counts plays and Loves, and Trending ranks on them — so the old sentence ("no play,
      like or save counts") is no longer true and had to go. What replaced it states the
      thing that IS still missing, which is the part that matters: the counts are this
      browser's, so the chart is one person's activity rather than a platform's. A blocker
      that overstates the gap gets deleted by the next person as obviously stale; one that
      understates it is worse, because the screen then implies a real chart.
    */
    id: 'no-recommendation-service',
    summary:
      'Plays and Loves are counted in this browser only, so Trending ranks one person’s activity rather than a platform’s.',
    resolution:
      'Record plays and Loves server-side, per account, and compute the Trending ranking there instead of ordering the local catalogue.',
  });

  return blockers;
}

export interface DiscoverySongsRequest extends PageRequest {
  genre?: string;
}

export const discoveryApi = {
  /**
   * What the Home page may claim. Read at render time, not cached.
   *
   * A module function rather than `this.describeDiscoveryBlockers()`, matching
   * generationApi: a method reaching through `this` breaks the moment a caller
   * destructures it, and this codebase has been bitten by exactly that once.
   */
  discoverySupport(): DiscoverySupport {
    const blockers = describeDiscoveryBlockers();
    return {
      catalogueAvailable: blockers.length === 0,
      rankingIsSocial: !blockers.some((b) => b.id === 'no-recommendation-service'),
      blockers,
    };
  },

  /**
   * MOCK: GET /discovery/showcase
   *
   * ==========================================================================
   * NO LIBRARY PARAMETER, AND THAT IS THE POINT (Batch 7 §1.6). This used to be
   * `getShowcase(songs, options)`, so the caller supplied the catalogue and the browser was
   * therefore the authoritative source of public visibility — a component could put any song
   * into the public lists by passing it in, and could keep one out by not. The catalogue now
   * comes from the mock's songs table and the eligibility rule is applied to it here.
   *
   * Which means the signature is already the real one. `GET /discovery/showcase?genre=…`
   * takes no body and no library, so replacing this body with a `fetch` changes nothing at
   * the call site — and the client cannot get a song listed except by publishing it.
   * ==========================================================================
   *
   * `options.genre` is the genre filter, applied to `explore` only — one request for the
   * whole screen rather than a second endpoint per section, because Featured, Trending and
   * Explore are three views of one catalogue and a filter that refetched only part of it
   * could show two of them disagreeing.
   *
   * `options.saves` is playlists-per-song, which the Featured ranking's third signal reads.
   * It is STILL a parameter, and it is now the only client-supplied input to the ranking: a
   * save is a playlist membership, and playlists are the caller's. The real endpoint must
   * count saves server-side and must NOT accept them from a client — a client-supplied
   * engagement figure is a client-editable ranking. That is the
   * `client-supplied-save-counts` blocker, stated on screen rather than only here. See
   * docs/api-contract.md §22.
   *
   * Deterministic — same table, same lists out — so the Home page does not reshuffle
   * between renders. Nothing is invented: every song returned is one the table holds.
   */
  async getShowcase(options: ShowcaseOptions = {}): Promise<Showcase> {
    if (!USE_MOCK_API) {
      const query =
        options.genre && options.genre !== 'All genres' ? { genre: options.genre } : undefined;
      const showcase = await http.get<Showcase>('/discovery/showcase', { auth: false, query });
      return withDemoSongStats(showcase);
    }
    assertMock('discoveryApi.getShowcase');
    /*
      THE FILTERING HAPPENS HERE, AT THE BOUNDARY, on the way out. `buildShowcase` applies
      `isPubliclyDiscoverable` to everything it is given, so a private song never reaches
      the returned lists — which is the same rule a real query must enforce in SQL, for the
      reason the `client-side-visibility-filter` blocker gives: a payload that arrives has
      already leaked.
    */
    const table = mockCatalogueSongs();
    const hydrated = hydratePersistedSongLengths(table);
    return mockDelay(withDemoSongStats(buildShowcase(hydrated, options)));
  },

  async getExploreSongs({ genre, limit, cursor }: DiscoverySongsRequest = {}): Promise<Page<Song>> {
    if (!USE_MOCK_API) {
      const query = {
        ...(genre && genre !== 'All genres' ? { genre } : null),
        ...(limit ? { limit } : null),
        ...(cursor ? { cursor } : null),
      };
      const page = await http.get<Page<Song>>('/discovery/songs', { auth: false, query });
      return withDemoSongStatsPage(page);
    }
    assertMock('discoveryApi.getExploreSongs');
    const showcase = buildShowcase(hydratePersistedSongLengths(mockCatalogueSongs()), { genre });
    const start = cursor
      ? Math.max(0, showcase.explore.findIndex((song) => String(song.seq) === cursor) + 1)
      : 0;
    const size = Math.max(1, limit ?? 8);
    const items = showcase.explore.slice(start, start + size);
    return mockDelay(
      withDemoSongStatsPage({
        items,
        nextCursor:
          items.length === size && showcase.explore[start + size]
            ? String(items[items.length - 1]?.seq ?? '')
            : null,
      })
    );
  },
};
